import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';

function TaskByClient(props) {
  const [companies, setCompanies] = React.useState([]);
  const [taskTypes, setTaskTypes] = React.useState([]);
  const [taskStages, setTaskStages] = React.useState([]);

  const [companyId, setCompanyId] = React.useState('');
  const [taskTypeId, setTaskTypeId] = React.useState('');
  const [taskStageId, setTaskStageId] = React.useState('');

  const [rows, setRows] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });

  // Companies
  React.useEffect(() => {
    getRequest(
      GET_URL.company.api,
      { limit: 1000, pageno: 1 },
      props
    ).then((response) => {
      if (response && response.status === 200) {
        setCompanies(response.data?.data || []);
      }
    });
  }, [props]);

  // Task Types
  React.useEffect(() => {
    getRequest(
      GET_URL.task_types.api,
      { limit: 1000, pageno: 1 },
      props
    ).then((response) => {
      if (response && response.status === 200) {
        setTaskTypes(response.data?.data || []);
      }
    });
  }, [props]);

  // Task Stages based on Task Type
  React.useEffect(() => {
    if (!taskTypeId) {
      setTaskStages([]);
      setTaskStageId('');
      return;
    }

    getRequest(
      GET_URL.task_type_stages.api,
      {
        limit: 1000,
        pageno: 1,
        task_type: taskTypeId,
      },
      props
    ).then((response) => {
      if (response && response.status === 200) {
        setTaskStages(response.data?.data || []);
      } else {
        setTaskStages([]);
      }
    });
  }, [taskTypeId, props]);

  // Tasks
  React.useEffect(() => {
    setLoading(true);

    const params = {
      limit: paginationModel.pageSize,
      pageno: paginationModel.page + 1,

      ...(companyId ? { company: companyId } : {}),
      ...(taskTypeId ? { task_type: taskTypeId } : {}),
      ...(taskStageId ? { task_stage: taskStageId } : {}),
    };

    getRequest(GET_URL.tasks.api, params, props)
      .then((response) => {
        if (response && response.status === 200) {
          setRows(response.data?.data || []);
          setRowCount(response.data?.count || 0);
        } else {
          setRows([]);
          setRowCount(0);
        }
      })
      .finally(() => setLoading(false));
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    props,
    companyId,
    taskTypeId,
    taskStageId,
  ]);

  const columns = [
    {
      field: 'company_name',
      headerName: 'Client',
      minWidth: 220,
      flex: 0.9,
      valueGetter: (params) => params.row.company_name || '—',
    },
    {
      field: 'title',
      headerName: 'Task',
      minWidth: 260,
      flex: 1.2,
    },
    {
      field: 'task_type_name',
      headerName: 'Task Type',
      minWidth: 160,
      flex: 0.6,
    },
    {
      field: 'priority',
      headerName: 'Priority',
      minWidth: 110,
      flex: 0.4,
    },
    {
      field: 'task_stage_name',
      headerName: 'Stage',
      minWidth: 130,
      flex: 0.5,
    },
    {
      field: 'sprint_name',
      headerName: 'Sprint',
      minWidth: 180,
      flex: 0.7,
    },
    {
      field: 'assigned_to_name',
      headerName: 'Assigned To',
      minWidth: 160,
      flex: 0.7,
    },
    {
      field: 'deadline',
      headerName: 'Deadline',
      minWidth: 120,
      flex: 0.5,
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700}>
        Tasks by Clients
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2 }}
      >
        Filter tasks by client, task type and stage.
      </Typography>

      <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Grid container spacing={2}>
          {/* Client Filter */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="company-label">
                Client
              </InputLabel>

              <Select
                labelId="company-label"
                label="Client"
                value={companyId}
                onChange={(e) => {
                  setCompanyId(e.target.value);
                  setPaginationModel((prev) => ({
                    ...prev,
                    page: 0,
                  }));
                }}
              >
                <MenuItem value="">All</MenuItem>

                {companies.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Task Type Filter */}
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="task-type-label">
                Task Type
              </InputLabel>

              <Select
                labelId="task-type-label"
                label="Task Type"
                value={taskTypeId}
                onChange={(e) => {
                  setTaskTypeId(e.target.value);
                  setTaskStageId('');

                  setPaginationModel((prev) => ({
                    ...prev,
                    page: 0,
                  }));
                }}
              >
                <MenuItem value="">All</MenuItem>

                {taskTypes.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Task Stage Filter */}
          <Grid item xs={12} md={4}>
            <FormControl
              fullWidth
              size="small"
              disabled={!taskTypeId}
            >
              <InputLabel id="task-stage-label">
                Task Stage
              </InputLabel>

              <Select
                labelId="task-stage-label"
                label="Task Stage"
                value={taskStageId}
                onChange={(e) => {
                  setTaskStageId(e.target.value);

                  setPaginationModel((prev) => ({
                    ...prev,
                    page: 0,
                  }));
                }}
              >
                <MenuItem value="">All</MenuItem>

                {taskStages.map((stage) => (
                  <MenuItem key={stage.id} value={stage.id}>
                    {stage.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={[10, 20, 50]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          disableRowSelectionOnClick
        />
      </Paper>
    </Box>
  );
}

export default TaskByClient;